import { useEffect } from 'react';

import {
  FIXED_ROOT_SELECTOR,
  OPEN_CLASS,
  STATIC_ROOT_SELECTOR,
  acquireSupportChat,
  identifySupportUser,
  releaseSupportChat,
  scopeSelector,
} from '@/lib/supportChat';

/**
 * Sube la burbuja por encima de la barra de navegacion inferior en movil. Sin
 * esto queda literalmente debajo de ella, invisible.
 *
 * El proveedor puede colgar del body mas de una caja y no todas se tratan igual,
 * asi que las reparte en dos grupos y aqui hay una regla para cada uno:
 *
 * - Las `static` no se pueden transformar tal cual: el `position: fixed` esta en
 *   sus hijos y transformar un ancestro no fijo se lo rompe, que pasarian a
 *   scrollear con la pagina. La salida es convertir el contenedor en una caja
 *   fija a pantalla completa y transformar esa: el boton se ancla a ella, que no
 *   scrollea, y solo se desplaza hacia arriba. El `pointer-events: none` impide
 *   que esa caja invisible se coma los clicks de la pagina entera (los botones
 *   de la barra inferior, sin ir mas lejos) y los hijos lo restauran.
 * - Las que ya son `fixed` se transforman directamente, igual que el boton de
 *   CookieYes en `Layout.jsx`. Y NO pueden llevar el apaño anterior: la de
 *   Richpanel es un <iframe> que en nuestro documento no tiene ni un hijo, asi
 *   que el `> *` que devuelve los `pointer-events` no tendria donde agarrarse y
 *   quedaria un panel que no se puede tocar.
 *
 * En los dos casos se desactiva con el chat abierto: en movil el panel ocupa la
 * pantalla entera, y subirlo 90px dejaria una franja en blanco abajo.
 *
 * Sin acentos graves en este texto: vive dentro de un template literal.
 */
function buildLiftCss() {
  // Via `scopeSelector` y no concatenando: cada grupo puede ser una lista, y un
  // prefijo pegado por delante solo condicionaria al primero.
  const cerrado = `html:not(.${OPEN_CLASS})`;
  return `
    @media (max-width: 767px) {
      ${scopeSelector(STATIC_ROOT_SELECTOR, cerrado)} {
        position: fixed !important;
        inset: 0 !important;
        transform: translateY(var(--support-chat-lift)) !important;
        pointer-events: none !important;
      }
      ${scopeSelector(STATIC_ROOT_SELECTOR, cerrado, '> *')} {
        pointer-events: auto !important;
      }
      ${scopeSelector(FIXED_ROOT_SELECTOR, cerrado)} {
        transform: translateY(var(--support-chat-lift)) !important;
      }
    }
  `;
}

const LIFT_CSS = buildLiftCss();

/**
 * Widget flotante de chat de soporte.
 *
 * La burbuja se ve mientras este componente esta montado —el Layout de usuarios
 * logueados y las paginas publicas de marketing— y se esconde al desmontarse, de
 * modo que NO aparece en login, registro ni en las legales. El script se inyecta
 * una sola vez por sesion; a partir de ahi solo se muestra y se esconde.
 *
 * Que proveedor hay detras, como se le habla y como se llama su contenedor en el
 * DOM es cosa de `lib/supportChat`. Aqui no se nombra ninguno a proposito.
 *
 * En la app con sesión se monta con `launcher={false}`: no hay burbuja y el
 * chat se abre desde «Soporte» en el menú del avatar (`contactSupport`).
 *
 * @param {{
 *   user?: { id?: string, email?: string, username?: string, phone?: string,
 *            createdAt?: string | number, impersonation?: object | null } | null,
 *   billing?: object | null,
 *   liftAboveMobileNav?: boolean,
 *   launcher?: boolean,
 *   verifiedIdentity?: string | null,
 *   identityPending?: boolean,
 * }} props
 *   - `verifiedIdentity`: la identidad cifrada del servidor
 *     (`/auth/support-identity/`), para que el messenger no pida los datos.
 *   - `identityPending`: mientras sea `true` NO se carga el script. Richpanel
 *     lee la identidad al cargar, así que cargarlo antes de tenerla sería
 *     perderla hasta la siguiente recarga.
 */
export default function SupportChat({
  user = null,
  billing = null,
  liftAboveMobileNav = false,
  launcher = true,
  verifiedIdentity = null,
  identityPending = false,
}) {
  useEffect(() => {
    if (identityPending) return undefined;
    acquireSupportChat({ launcher, verifiedIdentity });
    return () => releaseSupportChat({ launcher });
    // `verifiedIdentity` fuera de las dependencias a propósito: solo cuenta en
    // la primera carga del script y volver a montar por ella no aporta nada.
  }, [identityPending, launcher]);

  // Identifica al usuario y adjunta los datos de su cuenta al panel de soporte.
  // Impersonando NO: el agente vería y escribiría la conversación del cliente
  // como si fuera él, y quedaría en su historial.
  const impersonating = Boolean(user?.impersonation);
  useEffect(() => {
    if (impersonating) return;
    identifySupportUser(user, billing);
  }, [user, billing, impersonating]);

  // El CSS de elevacion solo se monta donde hay barra inferior que esquivar. En
  // las paginas publicas no la hay, y la burbuja se queda en su sitio nativo.
  if (!liftAboveMobileNav) return null;
  return <style>{LIFT_CSS}</style>;
}
